import React from 'react'
import c from 'classnames'
import gql from 'graphql-tag'
import _camelCase from 'lodash/camelCase'
import { useGlobalContext } from '@/components/context'
import { showNotification } from '@/state/user-interface'
import { useAppDispatch } from '@/utils/hooks'
import iconStyle from '@sass/common/icon.module.sass'
import { UserToManageQuery } from '../__gen/edit'
import { TeamsToAssignQuery } from './__gen'
import { useCreateUserModuleMutation, useRemoveUserModuleMutation } from './__gen/module-item'
import style from './module-item.module.sass'
import TeamItem from './team-item'

type UserType = NonNullable<UserToManageQuery['user']>
type TeamType = NonNullable<TeamsToAssignQuery['team_List']>['objects'][number]
type UserModuleType = NonNullable<TeamsToAssignQuery['userModule_List']>['objects'][number]

gql`
	mutation createUserModule($module: ID!, $user: ID!) {
		userModule_Update(input: { module: $module, user: $user }) {
			object {
				id
				active
				module {
					id
				}
				user {
					id
				}
			}
		}
	}
`

gql`
	mutation removeUserModule($id: ID!) {
		userModule_Update(input: { id: $id, active: false }) {
			object {
				id
				active
			}
		}
	}
`

type Props = {
	editable: boolean
	module: TeamType['module']
	teams: TeamType[]
	userModule: UserModuleType | null
	user: UserType
	// Assigning a module changes every team in it, so the whole list is reloaded
	refetch: () => void
}

export default (props: Props) => {
	const [remove, removeDetails] = useRemoveUserModuleMutation()
	const [add, addDetails] = useCreateUserModuleMutation()
	const globalContext = useGlobalContext()
	const loading = removeDetails.loading || addDetails.loading
	const dispatch = useAppDispatch()

	const assigned = !!props.userModule
	const teamWord = props.module.teamWord.toLowerCase()
	const moduleWord = globalContext.organisation.suite.moduleWord
	const memberCount = props.teams.filter((t) => t.memberId).length

	if (!props.editable && !assigned && memberCount === 0) return null

	// module.icon needs to be translated to iconStyle.medium*
	const icon = _camelCase(props.module.icon?.replace('icon-', '')) as keyof typeof iconStyle
	const visibleTeams = props.editable || assigned ? props.teams : props.teams.filter((t) => t.memberId)

	const toggle = async () => {
		if (loading) return
		if (props.userModule) {
			await remove({ variables: { id: props.userModule.id } })
			dispatch(
				showNotification({
					content: `Removed ${props.user.firstName}'s full access to ${props.module.name}`,
				}),
			)
		} else {
			await add({ variables: { user: props.user.id, module: props.module.id } })
			dispatch(
				showNotification({
					content: `Gave ${props.user.firstName} full access to ${props.module.name}`,
				}),
			)
		}
		props.refetch()
	}

	return (
		<div className={c(style.card, { [style.cardAssigned]: assigned })}>
			<div className={style.header}>
				<div className={c(style.icon, icon && iconStyle[icon])} />
				<div className={style.title}>
					<div className={style.name}>
						{props.module.name}
					</div>
					<div className={style.summary}>
						{assigned
							? `Can manage every ${teamWord} in this ${moduleWord.toLowerCase()}`
							: `${memberCount} of ${props.teams.length} ${teamWord}${props.teams.length === 1 ? '' : 's'}`}
					</div>
				</div>
				{assigned && !props.editable && <span className={style.badge}>Full Access</span>}
				{props.editable && (
					<label className={style.switchLabel}>
						<span>Full access</span>
						<button
							type="button"
							role="switch"
							aria-checked={assigned}
							aria-label={`Full access to ${props.module.name}`}
							className={c(style.switch, {
								[style.switchOn]: assigned,
								[style.switchLoading]: loading,
							})}
							onClick={toggle}
						>
							<span className={style.thumb} />
						</button>
					</label>
				)}
			</div>

			{visibleTeams.length > 0 && (
				<div className={style.teams}>
					{visibleTeams.map((t) => (
						<TeamItem
							key={t.id}
							team={t}
							user={props.user}
							editable={props.editable}
							lockedReason={assigned ? `Included with full access to ${props.module.name}` : null}
						/>
					))}
				</div>
			)}
		</div>
	)
}
